import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { ProgressInsightsGrid, generateInsights } from './ProgressInsightCard';
import InterviewComparisonList from './InterviewComparisonList';
import ProgressChart from './ProgressChart';

const CHART_RANGE_OPTIONS = [
  { value: 'last10', label: 'Last 10 sessions' },
  { value: 'lastMonth', label: 'Last month' },
  { value: 'last3Months', label: 'Last 3 months' },
  { value: 'last6Months', label: 'Last 6 months' },
  { value: 'year', label: 'Last year' },
];

const buildChartPoint = (interview) => {
  const base = interview.overallScore;
  const tech = interview.scores?.find(s =>
    s.name?.toLowerCase().includes('coding') ||
    s.name?.toLowerCase().includes('technical') ||
    s.name?.toLowerCase().includes('problem')
  )?.current || base;
  const comm = interview.scores?.find(s =>
    s.name?.toLowerCase().includes('communication') ||
    s.name?.toLowerCase().includes('soft')
  )?.current || base;
  return {
    date: interview.date,
    overall: base,
    technical: Math.min(100, Math.max(0, tech)),
    communication: Math.min(100, Math.max(0, comm)),
  };
};

const filterInterviewsByRange = (interviews, range) => {
  if (!interviews?.length) return [];

  if (range === 'last10') {
    return interviews.slice(0, 10);
  }

  const now = new Date();
  const cutoff = new Date(now);

  switch (range) {
    case 'lastMonth':
      cutoff.setMonth(cutoff.getMonth() - 1);
      break;
    case 'last3Months':
      cutoff.setMonth(cutoff.getMonth() - 3);
      break;
    case 'last6Months':
      cutoff.setMonth(cutoff.getMonth() - 6);
      break;
    case 'year':
      cutoff.setFullYear(cutoff.getFullYear() - 1);
      break;
    default:
      return interviews.slice(0, 10);
  }

  return interviews.filter((i) => {
    const date = i.createdAt ? new Date(i.createdAt) : null;
    return date && !Number.isNaN(date.getTime()) && date >= cutoff;
  });
};

/**
 * ProgressJourney — the centerpiece. Shows:
 * 1. Latest vs Previous highlight banner
 * 2. Auto-generated progress insights
 * 3. Interactive comparison slider for all interview pairs
 */
const ProgressJourney = ({ interviews, rank, hasMore, loadingMore, loadMore, totalInterviews }) => {
  const navigate = useNavigate();
  const [showAllInsights, setShowAllInsights] = useState(false);
  const [chartRange, setChartRange] = useState('last10');

  const progressChartData = useMemo(() => {
    const filtered = filterInterviewsByRange(interviews, chartRange);
    const data = [...filtered].reverse().map(buildChartPoint);
    if (data.length === 0) {
      return [{ date: 'Start', overall: 0, technical: 0, communication: 0 }];
    }
    return data;
  }, [interviews, chartRange]);

  if (!interviews || interviews.length === 0) {
    return (
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-10 text-center relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-[radial-gradient(circle_at_top_right,rgba(16,185,129,0.08),transparent)] pointer-events-none" />
        <Icon name="PlayCircle" size={48} className="text-emerald-500/30 mx-auto mb-4" />
        <h3 className="text-lg font-bold text-white mb-2">Start Your Journey</h3>
        <p className="text-slate-400 text-sm mb-8 max-w-md mx-auto">
          Take your first mock interview to begin tracking your progress. We'll show you detailed
          insights, skill comparisons, and growth metrics after each session.
        </p>
        <button
          onClick={() => navigate('/interview-setup')}
          className="px-8 py-3.5 bg-emerald-500 text-white rounded-xl text-[11px] font-black tracking-widest hover:bg-emerald-400 transition-all"
        >
          <Icon name="Play" size={14} className="inline mr-2 fill-current" />
          Take First Interview
        </button>
      </div>
    );
  }

  const latest = interviews[0];
  const previous = interviews.length > 1 ? interviews[1] : null;
  const first = interviews.length > 2 ? interviews[interviews.length - 1] : previous;

  // Generate insights
  const allInsights = generateInsights(interviews, latest, previous, first);
  const displayInsights = showAllInsights ? allInsights : allInsights.slice(0, 4);

  // Hero delta
  const scoreDelta = previous ? (latest.overallScore || 0) - (previous.overallScore || 0) : 0;
  const latestFillers = latest.computedMetrics?.fillerWordCount;
  const prevFillers = previous?.computedMetrics?.fillerWordCount;
  const fillerDelta = (prevFillers !== undefined && latestFillers !== undefined)
    ? prevFillers - latestFillers : null;

  return (
    <div className="space-y-8">
      {/* ─── Hero: Latest Performance Summary ─── */}
      {previous && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-500/5 rounded-full -mr-20 -mt-20 blur-3xl" />

          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[9px] font-black text-emerald-600 tracking-[0.2em]">Last Session Summary</span>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Score change */}
              <div className="space-y-1">
                <p className="text-[11px] font-black text-slate-900 tracking-wider">Score Change</p>
                <div className="flex items-center gap-2">
                  <span className={`text-xl font-black tabular-nums ${scoreDelta >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                    {scoreDelta >= 0 ? '+' : ''}{scoreDelta}
                  </span>

                  <Icon
                    name={scoreDelta >= 0 ? 'TrendingUp' : 'TrendingDown'}
                    size={16}
                    className={scoreDelta >= 0 ? 'text-emerald-400' : 'text-rose-400'}
                  />
                </div>
                <p className="text-[10px] text-slate-400 font-medium">
                  {previous.overallScore} → {latest.overallScore}
                </p>
              </div>

              {/* Filler words change */}
              <div className="space-y-1">
                <p className="text-[11px] font-black text-slate-900 tracking-wider">Filler Words</p>
                <div className="flex items-center gap-2">
                  {fillerDelta !== null ? (
                    <>
                      <span className={`text-xl font-black tabular-nums ${fillerDelta >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                        {fillerDelta >= 0 ? '−' : '+'}{Math.abs(fillerDelta)}
                      </span>

                      <Icon
                        name={fillerDelta >= 0 ? 'TrendingDown' : 'TrendingUp'}
                        size={16}
                        className={fillerDelta >= 0 ? 'text-emerald-400' : 'text-rose-400'}
                      />
                    </>
                  ) : (
                    <span className="text-xl font-black text-slate-300">—</span>
                  )}
                </div>
                <p className="text-[10px] text-slate-400 font-medium">
                  {prevFillers ?? '—'} → {latestFillers ?? '—'}
                </p>
              </div>

              {/* Talk ratio */}
              <div className="space-y-1">
                <p className="text-[11px] font-black text-slate-900 tracking-wider">Talk Ratio</p>
                <div className="flex items-center gap-2">
                  <span className="text-xl font-black text-slate-800 tabular-nums">
                    {latest.computedMetrics?.talkRatio
                      ? `${Math.round(latest.computedMetrics.talkRatio * 100)}%`
                      : '—'}
                  </span>

                </div>
                <p className="text-[10px] text-slate-400 font-medium">
                  {latest.computedMetrics?.talkRatio
                    ? (Math.round(latest.computedMetrics.talkRatio * 100) >= 40 && Math.round(latest.computedMetrics.talkRatio * 100) <= 65
                        ? '✅ Ideal range' : 'Target: 40–65%')
                    : 'No data'}
                </p>
              </div>

              {/* Interview count */}
              <div className="space-y-1">
                <p className="text-[11px] font-black text-slate-900 tracking-wider">Total Sessions</p>
                <div className="flex items-center gap-2">
                  <span className="text-xl font-black text-slate-800 tabular-nums">{interviews.length}</span>

                </div>
                <p className="text-[10px] text-slate-400 font-medium">
                  interviews completed
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Performance Trend Chart (Above Insights) ─── */}
      <div className="bg-white rounded-2xl border border-slate-100 p-8">
        <div className="flex items-center justify-between mb-8 gap-4">
          <div>
            <h3 className="text-base font-bold text-slate-900">Performance Trend</h3>
            <p className="text-xs text-slate-400 mt-1">Growth trajectory across core competencies</p>
          </div>
          <div className="relative shrink-0">
            <select
              value={chartRange}
              onChange={(e) => setChartRange(e.target.value)}
              className="appearance-none pl-3 pr-8 py-1.5 bg-slate-50 border border-slate-100 rounded-lg text-[10px] font-black text-slate-600 cursor-pointer hover:border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
              aria-label="Chart time range"
            >
              {CHART_RANGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <Icon
              name="ChevronDown"
              size={10}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
          </div>
        </div>
        <ProgressChart data={progressChartData} />
      </div>

      {/* ─── Progress Insights ─── */}
      {allInsights.length > 0 && (
        <div>
          <ProgressInsightsGrid insights={displayInsights} />
          {allInsights.length > 4 && (
            <button
              onClick={() => setShowAllInsights(!showAllInsights)}
              className="mt-3 flex items-center gap-1.5 mx-auto text-[10px] font-black text-emerald-600 tracking-wider hover:text-emerald-500 transition-colors"
            >
              {showAllInsights ? 'Show Less' : `Show ${allInsights.length - 4} More Insights`}
              <Icon name={showAllInsights ? 'ChevronUp' : 'ChevronDown'} size={12} />
            </button>
          )}
        </div>
      )}

      {/* ─── Interview Comparison List (descending) ─── */}
      <InterviewComparisonList 
        interviews={interviews} 
        rank={rank} 
        hasMore={hasMore} 
        loadingMore={loadingMore} 
        loadMore={loadMore} 
        totalInterviews={totalInterviews}
      />
    </div>
  );
};

export default ProgressJourney;
